<script setup lang="ts">
    import { computed } from 'vue';
    import { OrthographicCamera } from 'three';
    import { stopAll } from '@/util';
    import { catstackOverlaySceneContext } from '@/views/dicoms/overlay/scene/overlaySceneContext';
    import { updateCatstackCrossSectionOutlines } from '@/views/dicoms/overlay/scene/catstackCrossSection';
    import { updateIndicatorLine } from '@/views/dicoms/overlay/scene/indicatorLine';
    import type { CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
    import { useOverlayStore } from '@/views/dicoms/overlay/overlayStore';
    import { updateOverlayCatstackImage } from '@/views/dicoms/overlay/scene/overlayCatstackImage';
    import { useScene } from '@/lib/threeScene/scene/useScene';
    import { attachRenderingState } from '@/lib/threeScene/scene/attachRenderingState';
    import { renderingState } from '@/lib/threeScene/renderingState';

    interface Props {
        viewType: CatstackPlaneId;
        isSelected: boolean;
    }

    const props = defineProps<Props>();

    const overlayStore = useOverlayStore();

    const nodes = computed(() => overlayStore.getNodesForView(props.viewType));
    const sceneStateRef = computed(() => overlayStore.getSceneStateForView(props.viewType));

    function handleClick(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        overlayStore.setSelectedView(props.viewType);
    }

    const { containerRef, canvasRef } = useScene({
        camera: () => new OrthographicCamera(),
        sceneContext: () => {
            const ctx = catstackOverlaySceneContext(sceneStateRef.value);
            // Share with MainView, which reads back the same scene context.
            overlayStore.setSceneContextForView(props.viewType, ctx);
            return ctx;
        },
        setup: ({ canvas, container, sceneContext, camera, runtime }) => {
            // The camera state (position, target, zoom) is shared with the
            // MainView via the overlay store; only the per-view RenderingState
            // wrapper is local (its viewportSize is per-canvas).
            const state = renderingState({ camera: nodes.value.camera });
            return stopAll(
                updateOverlayCatstackImage(sceneContext, overlayStore, nodes.value.catStack),
                updateCatstackCrossSectionOutlines(sceneContext, overlayStore, props.viewType),
                updateIndicatorLine(sceneContext, overlayStore, props.viewType),
                attachRenderingState({
                    runtime,
                    state,
                    scene: sceneContext.scene,
                    camera,
                    container,
                    canvas,
                }),
            );
        },
    });
</script>

<template>
    <div
        ref="containerRef"
        class="side-view"
        :class="{ selected: isSelected }"
        @click="handleClick"
    >
        <canvas ref="canvasRef" class="side-canvas" />
        <div class="view-label">
            {{ viewType.toUpperCase() }}
        </div>
    </div>
</template>

<style scoped lang="scss">
    .side-view {
        width: 150px;
        height: 150px;
        min-height: 150px;
        min-width: 150px;
        position: relative;
        border-radius: 4px;
        overflow: hidden;
        background-color: rgb(var(--v-theme-scene-background));
        cursor: pointer;
        transition: all 0.3s ease;
        border: 2px solid #444;

        &:hover {
            border-color: #666;
        }

        &.selected {
            border-color: rgb(var(--v-theme-primary)) !important;
            box-shadow: 0 0 10px rgb(var(--v-theme-primary));
        }
    }

    .side-canvas {
        position: absolute;
        width: 100%;
        height: 100%;
        pointer-events: none;
    }

    .view-label {
        position: absolute;
        top: 8px;
        left: 8px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: bold;
        pointer-events: none;
        z-index: 10;
    }
</style>
